import { DashboardCardStatusKeys } from '../api/requests/types'

const getAPIURL = () => {
  if (import.meta.env.VITE_API_SAME_HOSTNAME) {
    const hostname = window.location.hostname
    return `http://${hostname}:${import.meta.env.VITE_API_PORT}`
  } else {
    return import.meta.env.VITE_API_URL
  }
}

export const API_URL = getAPIURL()
export const IS_LOCAL = import.meta.env.VITE_ENV === 'local'
export const DEFAULT_TIMEZONE = 'America/Chicago'
export const MAX_FILE_UPLOAD_SIZE_MB = 100 // 100MB
export const MAX_FILE_UPLOAD_SIZE = MAX_FILE_UPLOAD_SIZE_MB * 1024 ** 2 // 100MB
export const DIAGNOSTIC_ISSUE_TEMP_ID = 'df05d9ed-0afc-4e77-abaf-69f0630f3531'
export const FULL_GENERAL_DIAGNOSTIC_SERVICE_NAME = 'Full general diagnostic'
export const MAX_COLD_CRANKING_AMPS = 2000
export const MAX_TREAD_DEPTH = 15 // measured in 1/32 in
export const MOBILE_TREAD_DEPTH_LABELS = {
  immediate_attention: '0/32"-2/32" OR other issue',
  attention_soon: '3/32"-4/32" OR other issue',
  ok: '5/32"+',
  'n/a': 'NOT APPLICABLE',
  '': '',
}
export const DESKTOP_TREAD_DEPTH_LABELS = {
  immediate_attention: '0/32"-2/32" OR other issue',
  attention_soon: '3/32"-4/32" OR other issue',
  ok: '5/32"+',
  'n/a': 'N/A',
  '': '',
}
export const TREAD_DEPTH_THRESHOLDS = {
  attention_soon: 3,
  ok: 5,
}
export const MAX_BRAKE_PAD_THICKNESS = 32 // measured in mm
export const MOBILE_BRAKE_PAD_LABELS = {
  immediate_attention: '0-2mm OR other issue',
  attention_soon: '3-4mm OR other issue',
  ok: '5mm+',
  'n/a': 'NOT APPLICABLE',
  '': '',
}
export const DESKTOP_BRAKE_PAD_LABELS = {
  immediate_attention: '0-2mm OR other issue',
  attention_soon: '3-4mm OR other issue',
  ok: '5mm+',
  'n/a': 'N/A',
  '': '',
}
export const BRAKE_PAD_THRESHOLDS = {
  attention_soon: 3,
  ok: 5,
}

export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov']

export const TEMPORARY_INSPECTION_MEDIA_FILES_REFETCH_INTERVAL = 3000

export const VEHICLE_ENGINES = ['GAS', 'DIESEL', 'ELECTRIC'] as const
export type VehicleEngine = (typeof VEHICLE_ENGINES)[number]

export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number]

// Hardcoded time slots from 12:00 AM to 11:30 PM in 30-minute intervals.
// isoValue is used for lookup - we use HH:MM AM|PM format in the backend.
export const TIME_SLOTS = [
  { isoValue: '00:00', value: '12:00 AM', label: '12:00 AM' },
  { isoValue: '00:30', value: '12:30 AM', label: '12:30 AM' },
  { isoValue: '01:00', value: '01:00 AM', label: '1:00 AM' },
  { isoValue: '01:30', value: '01:30 AM', label: '1:30 AM' },
  { isoValue: '02:00', value: '02:00 AM', label: '2:00 AM' },
  { isoValue: '02:30', value: '02:30 AM', label: '2:30 AM' },
  { isoValue: '03:00', value: '03:00 AM', label: '3:00 AM' },
  { isoValue: '03:30', value: '03:30 AM', label: '3:30 AM' },
  { isoValue: '04:00', value: '04:00 AM', label: '4:00 AM' },
  { isoValue: '04:30', value: '04:30 AM', label: '4:30 AM' },
  { isoValue: '05:00', value: '05:00 AM', label: '5:00 AM' },
  { isoValue: '05:30', value: '05:30 AM', label: '5:30 AM' },
  { isoValue: '06:00', value: '06:00 AM', label: '6:00 AM' },
  { isoValue: '06:30', value: '06:30 AM', label: '6:30 AM' },
  { isoValue: '07:00', value: '07:00 AM', label: '7:00 AM' },
  { isoValue: '07:30', value: '07:30 AM', label: '7:30 AM' },
  { isoValue: '08:00', value: '08:00 AM', label: '8:00 AM' },
  { isoValue: '08:30', value: '08:30 AM', label: '8:30 AM' },
  { isoValue: '09:00', value: '09:00 AM', label: '9:00 AM' },
  { isoValue: '09:30', value: '09:30 AM', label: '9:30 AM' },
  { isoValue: '10:00', value: '10:00 AM', label: '10:00 AM' },
  { isoValue: '10:30', value: '10:30 AM', label: '10:30 AM' },
  { isoValue: '11:00', value: '11:00 AM', label: '11:00 AM' },
  { isoValue: '11:30', value: '11:30 AM', label: '11:30 AM' },
  { isoValue: '12:00', value: '12:00 PM', label: '12:00 PM' },
  { isoValue: '12:30', value: '12:30 PM', label: '12:30 PM' },
  { isoValue: '13:00', value: '01:00 PM', label: '1:00 PM' },
  { isoValue: '13:30', value: '01:30 PM', label: '1:30 PM' },
  { isoValue: '14:00', value: '02:00 PM', label: '2:00 PM' },
  { isoValue: '14:30', value: '02:30 PM', label: '2:30 PM' },
  { isoValue: '15:00', value: '03:00 PM', label: '3:00 PM' },
  { isoValue: '15:30', value: '03:30 PM', label: '3:30 PM' },
  { isoValue: '16:00', value: '04:00 PM', label: '4:00 PM' },
  { isoValue: '16:30', value: '04:30 PM', label: '4:30 PM' },
  { isoValue: '17:00', value: '05:00 PM', label: '5:00 PM' },
  { isoValue: '17:30', value: '05:30 PM', label: '5:30 PM' },
  { isoValue: '18:00', value: '06:00 PM', label: '6:00 PM' },
  { isoValue: '18:30', value: '06:30 PM', label: '6:30 PM' },
  { isoValue: '19:00', value: '07:00 PM', label: '7:00 PM' },
  { isoValue: '19:30', value: '07:30 PM', label: '7:30 PM' },
  { isoValue: '20:00', value: '08:00 PM', label: '8:00 PM' },
  { isoValue: '20:30', value: '08:30 PM', label: '8:30 PM' },
  { isoValue: '21:00', value: '09:00 PM', label: '9:00 PM' },
  { isoValue: '21:30', value: '09:30 PM', label: '9:30 PM' },
  { isoValue: '22:00', value: '10:00 PM', label: '10:00 PM' },
  { isoValue: '22:30', value: '10:30 PM', label: '10:30 PM' },
  { isoValue: '23:00', value: '11:00 PM', label: '11:00 PM' },
  { isoValue: '23:30', value: '11:30 PM', label: '11:30 PM' },
] as const

export const IS_ALL_PARTNERS_PROMO_ENABLED = true

export const DASHBOARD_CARD_TIMER_STATUSES_WITH_TIMER: DashboardCardStatusKeys[] = [
  'active_request',
  'diagnostic_and_inspection_action_required',
  'service_in_progress',
]
